import { useMutation } from '@tanstack/react-query';
import { useApiClientContext } from '../../surveys/context';
import { ICustomSurveyResponse, IOperationResultNoValue } from '../../surveys/models/Api';

/**
 * React-Query mutation hook for updating an existing custom survey response **draft**.
 *
 * Hits `PUT survey/custom/response/draft?token={accessToken}`.
 * The body is the partial `ICustomSurveyResponse` to merge into the draft on the BE.
 * Returns the updated `ICustomSurveyResponse`.
 */
export const useClientUpdateCustomSurveyResponseDraftMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: IOperationResultNoValue) => void;
	onSuccess?: (data?: ICustomSurveyResponse) => void;
}) => {
	const { apiClient } = useApiClientContext();

	return useMutation({
		mutationFn: ({ body, accessToken }: { accessToken: string; body: ICustomSurveyResponse }) => {
			// `accessToken` is a URL-safe JWT (base64url segments joined by dots), so it needs no
			// encodeURIComponent — and it must stay raw to match the sibling response hooks and the
			// finalize POST, all of which interpolate the same token unencoded.
			return apiClient.put<ICustomSurveyResponse>(`survey/custom/response/draft?token=${accessToken}`, body);
		},
		onError,
		onSuccess,
	});
};
